import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { ActionModuleResponse } from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { fmtDate } from '@/pages/incentivation/lib'
import { SUBTYPE_RENDERERS } from '@/pages/action-modules/components/subtypeRenderers'
import type { IncentivationFilterValues } from './IncentivationFilters'
import { ScoreCell } from './ScoreCell'
import { ScoreEditCell, type RewardCalculationInformation } from './ScoreEditCell'
import { useInstances } from './useInstances'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

interface Props {
  module: ActionModuleResponse
  employeeId: string
  instanceId: number
  applied: IncentivationFilterValues
  /** When true the Score row becomes an input for this instance (any instance of an active
   *  employee is editable; the tab passes this pre-gated on the employee's status). */
  editMode: boolean
  /** Per-module input type for the editable Score value. */
  dataType: IncentivationDataType
  /** This instance's draft reward info, if any. */
  draft: RewardCalculationInformation | undefined
  /** Reports a dirty change (info) or a revert-to-original (null) up to the tab. */
  onDraftChange: (employeeUserId: string, instanceId: number, info: RewardCalculationInformation | null) => void
  /** X in the header steps back to the instance list (clears the instance selection). */
  onClose: () => void
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{children}</span>
    </div>
  )
}

/**
 * Column 3 of the drill-down: read-only details of the selected instance. Left = the
 * subtypes and their entered values (reusing the shared `subtypeRenderers` Details), a
 * vertical divider, then instance metadata (completion date, rewarded points, score,
 * reward status). No action buttons in this slice.
 */
export function InstanceDetailsColumn({ module, employeeId, instanceId, applied, editMode, dataType, draft, onDraftChange, onClose }: Props) {
  const { t } = useTranslation('action-modules-detail')
  // Subtype renderer keys live in the incentivation namespace; pass that `t` to them.
  const { t: tInc } = useTranslation('incentivation')
  const { instances, isLoading } = useInstances(module.id ?? 0, employeeId, applied)

  const instance = instances.find((inst) => inst.id === instanceId)
  const subtypes = module.subtypes ?? []
  const subtypeData = instance?.subtypeData ?? []
  const rewardStatusLabel = instance
    ? t(`incentivation.rewardStatus.${instance.rewardStatus}`, { defaultValue: instance.rewardStatus })
    : '—'

  return (
    <div className="relative flex min-h-full flex-col">
      {/* Close X inside the view, pinned to the panel's top-right corner. */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 z-20 size-6"
        aria-label={t('incentivation.close')}
        title={t('incentivation.close')}
        onClick={onClose}
      >
        <X className="size-4" />
      </Button>
      {isLoading ? (
        <LoaderGeneral isLoading />
      ) : !instance ? (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {t('incentivation.noInstances')}
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4 p-4 pr-10 md:flex-row">
          {/* Subtypes + entered values */}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <p className="text-sm font-semibold">{t('incentivation.details.title')}</p>
            {subtypes.map((definition: any, i) => {
              const data = subtypeData.find((d) => d.moduleSubtypeId === definition.id)
              const renderer = SUBTYPE_RENDERERS[definition.subtype]
              if (!data || !renderer) return null
              return (
                <div key={definition.id ?? i} className="flex flex-col gap-1.5">
                  {definition.name && <p className="text-sm font-medium">{definition.name}</p>}
                  {renderer.Details({ definition, data, t: tInc })}
                </div>
              )
            })}
            {subtypeData.length === 0 && (
              <p className="text-sm italic text-muted-foreground">{tInc('subtype.noData')}</p>
            )}
          </div>

          <Separator orientation="vertical" className="hidden h-auto md:block" />

          {/* Instance metadata */}
          <div className="flex w-full flex-col gap-3 md:w-48 md:shrink-0">
            <p className="text-sm font-semibold">{t('incentivation.details.metadata')}</p>
            <MetaRow label={t('incentivation.details.completedAt')}>{fmtDate(instance.completedAt)}</MetaRow>
            <MetaRow label={t('incentivation.details.rewardedPoints')}>{instance.incentivationData?.reward ?? '—'}</MetaRow>
            <MetaRow label={t('incentivation.details.score')}>
              {editMode
                // Key by instance so the input re-seeds when a different instance is selected.
                ? <ScoreEditCell
                    key={instanceId}
                    dataType={dataType}
                    data={instance.incentivationData}
                    draft={draft}
                    onChange={(info) => onDraftChange(employeeId, instanceId, info)}
                  />
                : <ScoreCell data={instance.incentivationData} />}
            </MetaRow>
            <MetaRow label={t('incentivation.details.rewardStatus')}>{rewardStatusLabel}</MetaRow>
          </div>
        </div>
      )}
    </div>
  )
}
